import { LoadingOutlined } from "@ant-design/icons";
import { Switch, TableColumnsType } from "antd";
import moment from "moment";

import { IShortTermIncentive, IVersion } from "@types";
import { checkPermission } from "@/utils";
import { permissions } from "@/router";
import { dateFormat, roles } from "@/constants";
import { formatNumberPlain } from "@/utils/formatNumberWithCommas";
import { MutableRefObject } from "react";
import { MdOutlineFileDownload } from "react-icons/md";

export const getColumns = ({
  removeShortTermIncentive,
  shortTermIncentive_id,
  isDeleting,
  editShortTermIncentive,
  userRole,
}: {
  removeShortTermIncentive: (id: number) => Promise<void>;
  shortTermIncentive_id: React.MutableRefObject<any>;
  isDeleting: boolean;
  editShortTermIncentive: (incentive: IShortTermIncentive) => void;
  userRole: string;
}): TableColumnsType<any> => [
    {
      title: "plan name",
      key: "name",
      width: 200,
      sorter: true,
      align: "center",
      render: (incentive: IShortTermIncentive) => incentive?.plan?.name,
    },
    {
      title: "grade",
      key: "grade",
      width: 200,
      sorter: true,
      align: "center",
      render: (incentive: IShortTermIncentive) => {
        return incentive?.all_plan_grades
          ? "All Grades"
          : incentive?.grade || "";
      },
    },
    {
      title: "country",
      key: "country",
      width: 200,
      sorter: true,
      align: "center",
      render: (incentive: IShortTermIncentive) => {
        const isOnlyCompanyUser = roles.onlyCompany.includes(userRole);

        return (
          <div className="text-wrap">
            {isOnlyCompanyUser
              ? typeof incentive?.country == "string"
                ? incentive?.country
                : incentive?.country?.name
              : typeof incentive?.country == "string"
                ? incentive?.country
                : incentive?.country?.name}
          </div>
        );
      },
    },
    {
      title: "function",
      dataIndex: "job_function",
      key: "job_function",
      width: 200,
      align: "center",
      render: (job_function: any) => (
        <div>{job_function?.name || "-"}</div>
      ),
      sorter: true,
      align: "center",
    },
    {
      title: "sub function",
      dataIndex: "sub_job_function",
      key: "sub_job_function",
      width: 200,
      align: "center",
      render: (sub_job_function: any) => {
        if (!sub_job_function) {
          return "-";
        }

        if (typeof sub_job_function === "string") {
          return <div className="text-wrap">{sub_job_function}</div>;
        }

        if (Array.isArray(sub_job_function)) {
          return (
            <div className="text-wrap">
              {sub_job_function.length
                ? sub_job_function
                  .map(item => item?.name)
                  .filter(Boolean)
                  .join(", ")
                : "-"}
            </div>
          );
        }

        if (typeof sub_job_function === "object") {
          return (
            <div className="text-wrap">
              {sub_job_function?.name || "-"}
            </div>
          );
        }

        return "-";
      },
      sorter: true,
      align: "center",
    },
    {
      title: "amount or percentage",
      dataIndex: "is_percentage",
      key: "is_percentage",
      width: 250,
      sorter: true,
      align: "center",
      render: (is_percentage: boolean) =>
        is_percentage ? "Percentage" : "Amount",
    },
    {
      title: "Value",
      key: "value",
      sorter: true,
      width: 200,
      align: "center",
      render: (shortTermIncentive: IShortTermIncentive) =>
        shortTermIncentive?.is_percentage
          ? `${formatNumberPlain(shortTermIncentive?.value)}%`
          : formatNumberPlain(shortTermIncentive?.value),
    },


    {
      title: "basic pay or fixed pay",
      dataIndex: "is_basic_pay",
      key: "is_basic_pay",
      width: 250,
      sorter: true,
      align: "center",
      render: (is_basic_pay: boolean) => (is_basic_pay ? "Basic" : "Fixed"),
    },
    ...(!checkPermission([
      permissions.DELETE_COMPANY_STI,
      permissions.UPDATE_COMPANY_STI,
    ])
      ? []
      : ([
        {
          title: "Actions",
          key: "actions",
          width: 160,
          fixed: "right",
          align: "center",
          align: "center",
          render: (incentive: IShortTermIncentive) => (
            <div className="flex items-center gap-2">
              {checkPermission(permissions.UPDATE_COMPANY_STI) && (
                <div
                  onClick={() => editShortTermIncentive(incentive)}
                  className="table__action__btn table__action__btn--client"
                >
                  Edit
                </div>
              )}
              {checkPermission(permissions.DELETE_COMPANY_STI) && (
                <div
                  onClick={() => removeShortTermIncentive(incentive?.id)}
                  className="table__action__btn table__action__btn--delete"
                >
                  {isDeleting &&
                    incentive?.id === shortTermIncentive_id?.current ? (
                    <LoadingOutlined color="red" className="spinner" />
                  ) : (
                    "Delete"
                  )}
                </div>
              )}
            </div>
          ),
        },
      ] as any)),
  ];

type DownloadArgs = {
  company_id: number;
  version_id: number | null;
};

export const getVersionsColumns = ({
  handleActive,
  versionId,
  company_id,
  downloadShortTermIncentives,
  downloadArgs,
  isDownloading,
}: {
  handleActive: (id: number) => Promise<void>;
  versionId: number | null;
  company_id: number;

  downloadShortTermIncentives: (
    args: DownloadArgs
  ) => any;

  downloadArgs?: DownloadArgs;
  isDownloading: boolean;
}): TableColumnsType<any> => [
    {
      title: "created at",
      dataIndex: "created_at",
      key: "created_at",
      width: 240,
      align: "center",
      render: (date: string) => (
        <div>{moment(date).format(dateFormat)}</div>
      ),
    },

    {
      title: "updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 240,
      align: "center",
      render: (date: string) => (
        <div>{moment(date).format(dateFormat)}</div>
      ),
    },

    ...(!checkPermission(permissions.CREATE_COMPANY_STI)
      ? []
      : ([
        {
          title: "Active",
          key: "active",
          width: 140,
          align: "center",
          render: (version: IVersion) => (
            <Switch
              onChange={checked => {
                if (!checked) return;
                handleActive(version?.id);
              }}
              checked={versionId === version?.id}
              defaultChecked={version?.is_active === "TRUE"}
            />
          ),
        },
      ] as any)),

    {
      title: "",
      key: "spacer",
    },

    {
      title: "Actions",
      key: "action",
      width: 180,
      align: "center",

      render: (version: IVersion) => {
        const isCurrentRowDownloading =
          isDownloading &&
          downloadArgs?.version_id === version?.id;

        return (
          <div className="flex items-center justify-center">
            <div
              onClick={() => {
                if (!isDownloading) {
                  downloadShortTermIncentives({
                    company_id,
                    version_id: version?.id,
                  });
                }
              }}
              className="table__action__btn table__action__btn--download"
            >
              {isCurrentRowDownloading ? (
                <LoadingOutlined className="spinner" />
              ) : (
                <>
                  <MdOutlineFileDownload className="text-white text-xl mr-1" />
                  Download
                </>
              )}
            </div>
          </div>
        );
      },
    },
  ];